export type UseCaseStep = Record<string, unknown> & {description: string};
export type SnapshotChange = "added" | "removed" | "edited";

export const isUseCaseStep = (value: unknown): value is UseCaseStep =>
   !!value && typeof value === "object" && !Array.isArray(value) && typeof (value as {description?: unknown}).description === "string";

/** Text script names are only worth a request when a snapshot references one. */
export function referencesTextScript(...values: unknown[]): boolean {
   const seen = new Set<object>();
   const visit = (value: unknown): boolean => {
      if (!value || typeof value !== "object" || seen.has(value)) return false;
      seen.add(value);
      if (Array.isArray(value)) return value.some(visit);
      const record = value as Record<string, unknown>;
      if (Object.hasOwn(record, "textScriptId") && typeof record.textScriptId === "string") return true;
      return Object.keys(record).some(key => visit(record[key]));
   };
   return values.some(visit);
}

/** An Action's top-level `description` is its objective; inside a step it is the step itself. */
export const actionFieldLabel = (field: string) => field === "description" ? "objective" : field;

/** A field missing from one snapshot was added or removed, not edited. */
export function fieldChange(before: Record<string, unknown>, after: Record<string, unknown>, field: string): SnapshotChange {
   if (!Object.hasOwn(before, field)) return "added";
   if (!Object.hasOwn(after, field)) return "removed";
   return "edited";
}

export const CHANGE_HIGHLIGHT: Record<SnapshotChange, string> = {
   added: "bg-green-50 dark:bg-green-950/40",
   removed: "bg-red-50 dark:bg-red-950/40",
   edited: "bg-blue-50 dark:bg-blue-950/40",
};

export const CHANGE_BADGE: Record<SnapshotChange, string> = {
   added: "bg-green-50 text-green-700 dark:bg-green-950 dark:text-green-200",
   removed: "bg-red-50 text-red-700 dark:bg-red-950 dark:text-red-200",
   edited: "bg-blue-50 text-blue-700 dark:bg-blue-950 dark:text-blue-200",
};
